import { Component, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { OrganisationNameService } from 'app/shared/services/workspace/organisation-name.service';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppShellService, PersonaRole } from '../../../core/app-shell/app-shell.service';
import { AuthService } from '../../../core/auth/auth.service';
// GAP-MEET-UX-030 — badge counts opportunities, so it reads the deal list.
import { DealService } from '../../services/deal.service';
// SESSION-webplanner-005 / GAP-WEBPLANNER-005 — Queue badge wired to real meeting count.
import { MeetingService } from '../../../shared/apis/meeting/api/meeting.service';
// GAP-MEET-UX-019 — the badge and the /queue page must derive their count
// from the same rules. See shared/utils/qualification-workload.ts.
import { qualificationRowCount } from '../../utils/qualification-workload';
import { QueueChangeService } from '../../services/queue-change.service';
// SPEC-web-planner-026 AC-2 (GAP-MEET-UX-315): the CRO nav follows the client's plan.
import { WorkspaceService } from '../../services/workspace/workspace.service';
import { planOffersCro } from '../../services/workspace/persona-plans';

interface NavItem {
  id: string;
  label: string;
  route: string;
  icon: string;
  badge?: number;
  // SESSION-webplanner-002 / GAP-WEBPLANNER-002: nested sub-nav (Accounts ▸
  // Contacts · My Sessions ▸ New session). Proto §3.1 of
  // Web-planner-Designs/Inside-Sales-Implementation-Prompt.md.
  children?: NavItem[];
}

interface RoleConfig {
  label: string;
  nav: NavItem[];
  library: NavItem[];
}

// GAP-MEET-UX-213 — lifted to a module-level exported const so
// `app-shell-route-reachability.spec.ts` can read it without going through
// DI / component instantiation. The component below keeps consuming it
// unchanged.
// SPEC-web-planner-009 AC-7 / AC-8 — this table carries NO identity (name,
// initials) and NO static badge. The user card is the signed-in
// user; a badge exists only as a live count.
export const ROLES_MAP: Record<PersonaRole, RoleConfig> = {
  is: {
    label: 'Inside Sales',
    // SESSION-meet-ux-017 (Phase 1) / GAP-MEETUX-IS-001: IS nav restructured
    // around the agent-driven flow — Home → Queue → Accounts → Contacts →
    // My Sessions. /schedule (phantom) dropped (the schedule-is.html proto is
    // /sessions/new in Phase 2, not a top-level nav). Static badges removed —
    // counts come from real APIs once GAP-BACKEND-IS-002 lands.
    // SESSION-meet-ux-017 (Phase 2 visual-fidelity pass) / GAP-MEETUX-IS-001.
    // GAP-MEET-UX-019 (2026-07-30) — the static Queue badge `9` (the proto
    // demo-fixture count from `agent-home/shared.jsx`) is now REMOVED, as
    // that older comment asked once a live count existed. Leaving it meant
    // the sidebar rendered a fabricated 9 on every load until the fetch
    // resolved, and kept showing it forever when the fetch failed
    // (refreshQueueBadge is fail-silent). No badge until the real count
    // arrives is the honest state. Activity icon switched from `trending`
    // to the proto's `activity` (heartbeat/EKG) SVG.
    // SESSION-webplanner-002 / GAP-WEBPLANNER-002: nested per proto §3.1.
    // Accounts ▸ Contacts (canonical home for stakeholders), and My Sessions
    // ▸ New session (entry point to /sessions/new = SessionScheduleComponent).
    // The `sessions` icon switched from `play` (video-player glyph) to the
    // proto's `sessions` layered-triangle SVG — see icon ngSwitch in .html.
    // GAP-MEET-UX-213 (2026-09-26) — /schedule stays dropped for `is` for the
    // same reason it never existed here: it is a phantom route no routing
    // table registers (the `is` persona's schedule flow is /sessions/new).
    // This does not reintroduce it for any other persona either — fs's own
    // Schedule item (formerly /schedule) is removed below by the same GAP.
    nav: [
      { id: 'home', label: 'Home', route: '/home', icon: 'home' },
      { id: 'queue', label: 'Queue', route: '/queue', icon: 'queue' },
      { id: 'accounts', label: 'Accounts', route: '/accounts', icon: 'building', children: [
        { id: 'contacts', label: 'Contacts', route: '/contacts', icon: 'users' }
      ] },
      { id: 'sessions', label: 'My Sessions', route: '/sessions', icon: 'sessions', children: [
        { id: 'new-session', label: 'New session', route: '/sessions/new', icon: 'plus' }
      ] },
      { id: 'activity', label: 'Activity', route: '/activity', icon: 'activity' },
      // SPEC-web-planner-024 AC-6: the guide was only linked from empty states, and "How Awvent works" (/help) is gone.
      { id: 'getting-started', label: 'Getting started', route: '/getting-started', icon: 'help' }
    ],
    library: [
      { id: 'products', label: 'Products', route: '/products', icon: 'box' }
    ]
  },
  fs: {
    label: 'Field Sales',
    nav: [
      { id: 'deals', label: 'My Deals', route: '/deals', icon: 'briefcase' },
      { id: 'accounts', label: 'Accounts', route: '/accounts', icon: 'building' },
      { id: 'contacts', label: 'Contacts', route: '/contacts', icon: 'users' },
      { id: 'sessions', label: 'My Sessions', route: '/sessions', icon: 'play' }
    ],
    library: [
      { id: 'products', label: 'Products', route: '/products', icon: 'box' }
    ]
  },
  cro: {
    label: 'CRO',
    nav: [
      { id: 'portfolio', label: 'Portfolio', route: '/portfolio',     icon: 'pie' },
      { id: 'reps',      label: 'Reps',      route: '/reps',          icon: 'users' },
      { id: 'forecast',  label: 'Forecast',  route: '/forecast',      icon: 'trending' },
      { id: 'coaching',  label: 'Coaching',  route: '/coaching',      icon: 'megaphone' }
    ],
    library: [
      { id: 'products', label: 'Products', route: '/products', icon: 'box' }
    ]
  },
  admin: {
    label: 'Admin',
    nav: [
      { id: 'overview', label: 'Home', route: '/admin', icon: 'home' },
      { id: 'users', label: 'Users', route: '/admin/users', icon: 'users' },
      // SESSION-admin-ptg-004 — Teams & Groups page lives at the apex /teams
      // route (registered in app.routing.ts), not under /admin. Sidebar entry
      // updated to match so the active-nav state lights up correctly.
      { id: 'teams', label: 'Teams', route: '/teams', icon: 'shield' }
      // GAP-MEET-UX-213 (2026-09-26) — Billing (/admin/billing) and Data
      // (/admin/data) removed: neither path was registered by any routing
      // table, so both opened a blank shell. The operator decided admins
      // keep their working admin nav minus these two dead items; Billing and
      // Data are not being built by this change.
    ],
    library: [
      // SPEC-web-planner-035 AC-4: Workspace settings (Admin only), above Products as in the design.
      { id: 'settings', label: 'Settings', route: '/admin/settings', icon: 'settings' },
      { id: 'products', label: 'Products', route: '/products', icon: 'box' }
    ]
  }
};

@Component({
  selector: 'app-app-shell',
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.scss']
})
export class AppShellComponent implements OnInit, OnDestroy {
  /** SPEC-web-planner-008 AC-24 — the queue-changed signal's subscription (GAP-MEET-UX-283). */
  private queueChangeSub?: Subscription;
  /** AC-24 — the badge read in flight: a newer read cancels it (latest wins), and so does destroy. */
  private badgeRead?: Subscription;
  /** SPEC-web-planner-026 AC-2 — the CRO plan read in flight; a role change or destroy drops it. */
  private planRead?: Subscription;

  @Input() activeNav: string = 'home';

  // SESSION-meet-ux-004i / SIDEBAR-001 (AC-10): the futuristic layout now drives
  // the persona down via `[role]`. When a parent binds it, the setter takes
  // precedence and reacts to persona switches; when unbound (the existing
  // standalone consumers — meeting-list / meeting-details / admin-subscription)
  // it resolves from AppShellService in ngOnInit exactly as before, so their
  // `activeNav`-only contract is preserved (AC-18).
  @Input()
  set role(value: PersonaRole | null | undefined) {
    this._roleExplicitlySet = true;
    this.applyRole(value || this.appShellService.getPersonaRole());
  }
  get role(): PersonaRole {
    return this._role;
  }

  private _role: PersonaRole = 'is';
  private _roleExplicitlySet = false;

  roleConfig: RoleConfig | null = null;
  navItems: NavItem[] = [];
  libraryItems: NavItem[] = [];

  // SESSION-meet-ux-009 / USET-002: sidebar footer shows the signed-in user,
  // never a hardcoded value. Falls back to the role-config placeholder only
  // while the AuthService cache is warming (it is populated on sign-in).
  email: string = '';
  displayName: string = '';
  constructor(
    private appShellService: AppShellService,
    private authService: AuthService,
    // SESSION-webplanner-005 / GAP-WEBPLANNER-005 — refresh the IS Queue badge
    // with a real count. @Optional so unit tests can keep their existing
    // TestBed providers without stubbing MeetingService.
    @Optional() private meetingService?: MeetingService,
    // GAP-MEET-UX-030 — the badge counts opportunities, so it needs the deal
    // list. @Optional for the same reason as MeetingService: existing TestBeds
    // must keep working without stubbing it, and the count degrades to the
    // meeting-only path when it is absent.
    @Optional() private dealService?: DealService,
    // SPEC-web-planner-013 AC-82: the footer's organisation name.
    @Optional() private orgName?: OrganisationNameService,
    // SPEC-web-planner-008 AC-24 (GAP-MEET-UX-283): a saved decision re-reads the Queue badge. @Optional as above.
    @Optional() private queueChanges?: QueueChangeService,
    // SPEC-web-planner-026 AC-2: the CRO nav reads the plan. @Optional as above.
    @Optional() private workspaceService?: WorkspaceService
    // SPEC-web-planner-034 AC-4: the account menu (router, host, cdr) is removed.
  ) {}

  ngOnInit(): void {
    // SESSION-webplanner-002: pick up persisted collapse + nav-group state.
    this.restoreSidebarState();

    // SPEC-web-planner-008 AC-24 (GAP-MEET-UX-283): the badge was computed only when the persona was set, so a decision
    // that closed a deal left the old count until a reload. Re-read on each signal, for the IS persona (its only badge).
    this.queueChangeSub = this.queueChanges?.changed$.subscribe(() => {
      if (this._role === 'is') {
        this.refreshQueueBadge(true);
      }
    });

    // Only self-resolve when no parent bound `[role]`. The futuristic layout
    // binds it (and reacts to persona switches via the setter); the standalone
    // consumers do not, so they keep the prior AppShellService-driven behaviour.
    if (!this._roleExplicitlySet) {
      this.applyRole(this.appShellService.getPersonaRole());
    }

    // USET-002: bind name + email to the signed-in user. The role-config
    // demo name/email is only a last-resort placeholder until the cache hydrates.
    const user = this.authService.user;
    const first = user?.firstName?.trim() ?? '';
    const last = user?.lastName?.trim() ?? '';
    const composed = `${first} ${last}`.trim();
    this.displayName = composed || user?.displayName || '';
    this.email = user?.email?.value || '';
  }

  ngOnDestroy(): void {
    this.queueChangeSub?.unsubscribe();
    this.badgeRead?.unsubscribe();
    this.planRead?.unsubscribe();
  }

  /** Resolve the role + its nav config. Called from the `role` setter (parent
   * bound) and from ngOnInit (standalone fallback). */
  private applyRole(role: PersonaRole): void {
    this._role = role;
    this.roleConfig = ROLES_MAP[role] ?? null;
    // Copies, so a live badge set below never mutates the shared ROLES_MAP.
    const copy = (i: NavItem): NavItem => ({ ...i, children: i.children?.map(copy) });
    this.navItems = (this.roleConfig?.nav ?? []).map(copy);
    this.libraryItems = (this.roleConfig?.library ?? []).map(copy);
    this.planRead?.unsubscribe();
    if (role === 'cro') {
      this.applyCroPlan(copy);
    }
    // SESSION-webplanner-005 / GAP-WEBPLANNER-005 — refresh the IS Queue badge
    // with a real count from MeetingService. On failure the badge is ABSENT
    // (AC-7) — there is no static fallback to keep.
    if (role === 'is') {
      this.refreshQueueBadge();
    }
  }

  /**
   * SPEC-web-planner-026 AC-2 (GAP-MEET-UX-315): PersonaGuard admits a cro to the CRO pages only when the plan includes
   * cro (Top, or no plan set). So the four CRO entries show only once the plan read says so. While the read is pending
   * no CRO entry shows; when it fails (the guard fails closed) or the plan excludes cro, one Home entry (/home, the
   * guard's denial target) shows instead. The read is AC-1's shared one, so it costs no extra request.
   */
  private applyCroPlan(copy: (i: NavItem) => NavItem): void {
    const full = this.navItems;
    const home: NavItem = { id: 'home', label: 'Home', route: '/home', icon: 'home' };
    this.navItems = [];
    const workspaceId = this.authService?.selectedTenant?.id;
    if (!this.workspaceService || !workspaceId) { this.navItems = [copy(home)]; return; }
    this.planRead = this.workspaceService.getSeatLimit(workspaceId).subscribe((limit) => {
      // The same rule as PersonaGuard (one place, planOffersCro).
      if (limit && planOffersCro(limit.plan)) {
        this.navItems = full;
      } else {
        this.navItems = [copy(home)];
      }
    });
  }

  /** `afterChange` (AC-24): a re-read after a queue-changed signal keeps the badge it has when the deal read fails, since
   *  the meeting-only fallback cannot see a closed-lost deal and a blip must not clear a correct count. */
  private refreshQueueBadge(afterChange = false): void {
    if (!this.meetingService) { return; }
    const tenantId = this.authService?.selectedTenant?.id;
    if (!tenantId) { return; }
    // SPEC-webplanner-027 F1 — badge counts the rep's QUALIFICATION WORKLOAD,
    // not a tenant-wide Completed count. Previously this called
    // /Meeting/MeetingsCount?Status=Completed, but the backend MeetingsCount
    // handler ignores Status (and UserEmail), so the badge was the entire
    // tenant's meeting count regardless of rep or status.
    //
    // GAP-MEET-UX-019 (Prashant, 2026-07-30) — this used to "mirror the /queue
    // list logic" with its own inline `!== 'cancelled'` filter. That copy went
    // stale the moment GAP-MEET-UX-017 (drop upcoming) and GAP-MEET-UX-018
    // (collapse per opportunity) changed the list, and the sidebar read 14
    // against the page's "7 in qualification". The rules now come from
    // shared/utils/qualification-workload.ts — ONE derivation for both
    // surfaces, so the numbers cannot disagree again.
    // Same shape and page size the /queue page uses, then the shared count.
    // This replaced an earlier PageSize=1 probe + full-list pair: the probe only
    // existed to skip the second call when totalCount was 0, but it hit the same
    // endpoint the second call does, so it was a wasted round-trip every load.
    //
    // GAP-MEET-UX-030 — the queue counts OPPORTUNITIES now (every opportunity is
    // a row, including ones with no sessions yet), so the badge must read the
    // DEAL list, not the meeting list. Meetings are still fetched because
    // qualificationRowCount's meeting-only fallback is what keeps the badge
    // honest if the deal call fails.
    //
    // SPEC-web-planner-019 AC-26 (GAP-MEET-UX-243): the deals are the rep's OWN, read with the same listMine call the
    // queue uses, so the badge never counts another member's deal. With no user id there is no live count: no badge,
    // never a meeting-only count.
    const ownerUserId = this.authService?.user?.id;
    if (!ownerUserId) {
      this.badgeRead?.unsubscribe();   // an earlier read must not set a count for an unresolved user
      const queue = this.navItems.find(i => i.id === 'queue');
      if (queue) { queue.badge = undefined; }
      return;
    }
    const userEmail = this.authService?.user?.email?.value || null;
    const accessToken = this.authService?.accessToken || undefined;
    const read$ = forkJoin({
      meetings: this.meetingService.meetingGet(
        tenantId,
        userEmail, null, null, null, null,
        null,                     // Status — keep open; qualificationRowCount filters
        null, null, null, null,
        null, true,
        null, 1, 200,
      ).pipe(catchError(() => of(null))),
      deals: this.dealService
        ? this.dealService.listMine(tenantId, ownerUserId, accessToken).pipe(catchError(() => of(null)))
        : of(null),
    });
    this.badgeRead?.unsubscribe();
    this.badgeRead = read$.subscribe({
      next: ({ meetings, deals }: any) => {
        // AC-24 — after a change the deal list decides; without it, keep the badge rather than guess.
        if (afterChange && !deals) { return; }
        // AC-7 — both reads failed: there is no live count, so no badge.
        const queue = this.navItems.find(i => i.id === 'queue');
        if (!meetings && !deals) {
          if (queue) { queue.badge = undefined; }
          return;
        }
        const items = (meetings?.result || []) as any[];
        // `undefined` (not []) when deals are unavailable, so the count falls
        // back to the meeting-collapse path rather than reporting a real 0.
        const dealList = deals?.result ? (deals.result as any[]) : undefined;
        if (queue) { queue.badge = qualificationRowCount(items, dealList); }
      },
      error: () => {
        if (afterChange) { return; }
        const queue = this.navItems.find(i => i.id === 'queue');
        if (queue) { queue.badge = undefined; }
      }
    });
  }

  isNavActive(navId: string): boolean {
    return navId === this.activeNav;
  }

  // ── Sidebar collapse + nav-group accordion (SESSION-webplanner-002 / proto §3.4).
  // Both behaviours persist to localStorage so state survives reloads:
  //   awvent_sidebar_collapsed  → '1' | '0'
  //   awvent_nav_groups         → JSON { [groupId]: 'collapsed' | 'expanded' }
  // A group is force-expanded whenever it contains the active child, regardless
  // of stored state — matches the proto's `hasActiveChild` rule.
  private static readonly LS_COLLAPSE_KEY = 'awvent_sidebar_collapsed';
  private static readonly LS_GROUPS_KEY = 'awvent_nav_groups';

  collapsed = false;
  private groupState: Record<string, 'collapsed' | 'expanded'> = {};

  private restoreSidebarState(): void {
    try {
      this.collapsed = localStorage.getItem(AppShellComponent.LS_COLLAPSE_KEY) === '1';
    } catch { /* localStorage unavailable (SSR / disabled) — keep defaults */ }
    try {
      const raw = localStorage.getItem(AppShellComponent.LS_GROUPS_KEY);
      this.groupState = raw ? JSON.parse(raw) : {};
    } catch { this.groupState = {}; }
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    try { localStorage.setItem(AppShellComponent.LS_COLLAPSE_KEY, this.collapsed ? '1' : '0'); } catch {}
  }

  toggleGroup(id: string): void {
    const next = this.groupState[id] === 'collapsed' ? 'expanded' : 'collapsed';
    this.groupState = { ...this.groupState, [id]: next };
    try { localStorage.setItem(AppShellComponent.LS_GROUPS_KEY, JSON.stringify(this.groupState)); } catch {}
  }

  /** A group is collapsed only when stored state says so AND no child of it is the active page. */
  isGroupCollapsed(id: string): boolean {
    if (this.hasActiveChild(id)) { return false; }
    return this.groupState[id] === 'collapsed';
  }

  hasChildren(item: NavItem): boolean {
    return !!(item.children && item.children.length);
  }

  private hasActiveChild(groupId: string): boolean {
    const parent = this.navItems.find(n => n.id === groupId);
    if (!parent?.children) { return false; }
    return parent.children.some(c => c.id === this.activeNav);
  }

  // ── Persona block getters (SESSION-meet-ux-004i / AC-9). Copied verbatim from
  // the SESSION-004h persona-block fix on futuristic.component.ts so that
  // <app-app-shell> is self-sufficient — the sidebar foot + role chip no longer
  // depend on the layout passing display data down. ──────────────────────────

  /** Avatar palette class for the persona — `avatar-{amber|coral|purple|teal}`. */
  get personaTint(): 'amber' | 'coral' | 'purple' | 'teal' {
    switch (this._role) {
      case 'admin': return 'amber';
      case 'fs':    return 'coral';
      case 'cro':   return 'teal';
      default:      return 'purple';
    }
  }

  /** Canonical sentence-case role label matching the proto's `role.label`. */
  get roleLabel(): 'Admin' | 'Inside Sales' | 'Field Sales' | 'CRO' {
    switch (this._role) {
      case 'admin': return 'Admin';
      case 'fs':    return 'Field Sales';
      case 'cro':   return 'CRO';
      default:      return 'Inside Sales';
    }
  }

  /** Two-letter initials for the avatar — mirrors admin-home's orgMark logic. */
  get userInitials(): string {
    const user = this.authService.user;
    const first = user?.firstName?.trim() ?? '';
    const last = user?.lastName?.trim() ?? '';
    const fromParts = `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
    if (fromParts.length === 2) { return fromParts; }
    const composed = (user?.displayName ?? `${first} ${last}`).trim();
    const parts = composed.split(/\s+/).filter(Boolean);
    if (parts.length === 0) { return '?'; }
    if (parts.length === 1) { return parts[0].substring(0, 2).toUpperCase(); }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  /** "Pierre Desjardins" — full display name with cascading fallbacks
   * (firstName+lastName → displayName → 'Workspace'). Drives the role-chip name
   * slot (AC-2) and the footer name (AC-6). */
  get userDisplayName(): string {
    const user = this.authService.user;
    const first = user?.firstName?.trim() ?? '';
    const last = user?.lastName?.trim() ?? '';
    const composed = `${first} ${last}`.trim();
    return composed || user?.displayName || 'Workspace';
  }

  /** Workspace org name for the footer subtitle — e.g. "Prognosys Medical".
   * SPEC-web-planner-013 AC-82 (NC-8): the organisation, its tenant-entry name, or one read of
   * the client (OrganisationNameService); '' while unknown, never the 'Workspace' placeholder.
   * SPEC-web-planner-031 AC-5: the user card shows no territory and makes no Team read. */
  get workspaceLabel(): string {
    return this.orgName?.name() ?? '';
  }
}
